import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Gift, Loader2, ArrowRight, Star } from "lucide-react";

const BACKEND_URL = (typeof window !== 'undefined' && window.location.hostname === 'ambassador.eamico.com') ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function JoinPage() {
  const { code } = useParams();
  const [ambassador, setAmbassador] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successCode, setSuccessCode] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/ref/${code}`)
      .then(r => setAmbassador(r.data))
      .catch(() => setError("This referral link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/waitlist/signup`, { ...form, affiliate_code: code });
      if (response.data?.message) {
        setSuccessCode(response.data.waitlist_code || '');
        setSuccess(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail :
        Array.isArray(detail) ? detail.map(e => e?.msg || JSON.stringify(e)).join(' ') :
        "Something went wrong. Please try again.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  if (loading) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#F7941D] animate-spin" />
      </div>
    );
  }

  if (!ambassador && error) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center p-4">
        <div className="text-center max-w-md animate-fade-in-up">
          <div className="w-12 h-12 rounded-full bg-[#F7941D] flex items-center justify-center mx-auto mb-4 glow-orange">
            <span className="text-white text-lg font-bold">E</span>
          </div>
          <h1 className="text-xl font-semibold text-white/90 mb-2">Invalid Referral Link</h1>
          <p className="text-white/45 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center p-4" data-testid="waitlist-success">
        <div className="text-center max-w-md animate-fade-in-up">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white/90 mb-2">You're on the list!</h1>
          <p className="text-white/45 mb-6">
            Welcome to the Eamico Studio waitlist. Check your email for your activation code.
          </p>
          {successCode && (
            <div className="glass-strong rounded-2xl p-6 mb-6 glow-orange">
              <p className="text-white/35 text-xs uppercase tracking-widest mb-2">Your Activation Code</p>
              <p className="font-mono text-2xl font-bold text-[#F7941D] tracking-wider">{successCode}</p>
            </div>
          )}
          <div className="glass rounded-2xl p-4 inline-flex items-center gap-3">
            <Gift className="w-5 h-5 text-[#F7941D]" />
            <span className="text-sm text-white/70 font-medium">$50 Beta Credit Reserved</span>
          </div>
          <p className="text-xs text-white/25 mt-6">Save your code. Use it when Eamico Studio launches June 1st, 2026 to claim your $50 credit.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-bg" data-testid="join-page">
      {/* Header */}
      <header className="glass-strong border-b border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[#F7941D] flex items-center justify-center glow-orange">
              <span className="text-white text-sm font-bold">E</span>
            </div>
            <span className="text-lg font-semibold text-white/90 tracking-tight">Eamico</span>
          </div>
          <a href="https://eamico.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F7941D] hover:text-[#ff9f2e] font-medium transition-colors">
            Learn more <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
          </a>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-16 pb-8">
        <div className="max-w-2xl animate-fade-in-up">
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-6">
            <Gift className="w-4 h-4 text-[#F7941D]" />
            <span className="text-sm text-[#F7941D] font-medium">Get $50 credit at launch</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
            Join the Eamico Studio <span className="text-[#F7941D] text-glow">Beta</span>
          </h1>
          <p className="text-lg text-white/45 leading-relaxed max-w-xl">
            Be among the first to experience the cloud IDE that replaces your entire dev stack. Sign up and receive a $50 credit when Studio launches on June 1st, 2026.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            {ambassador?.tier && ambassador.tier !== "New" && (
              <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 mb-4">
                <Star className="w-3.5 h-3.5 text-[#F7941D]" fill="currentColor" />
                <span className="text-xs font-medium text-[#F7941D]">{ambassador.tier} Ambassador</span>
                {ambassador.total_signups > 0 && <span className="text-xs text-white/30">&middot; {ambassador.total_signups.toLocaleString()} referrals</span>}
              </div>
            )}

            {ambassador?.custom_message && (
              <div className="glass rounded-2xl p-4 mb-6">
                <p className="text-sm text-white/50 italic">"{ambassador.custom_message}"</p>
                <p className="text-xs text-white/25 mt-2">— {ambassador.display_name}</p>
              </div>
            )}

            <div className="glass-strong rounded-2xl p-6" data-testid="waitlist-form-card">
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-testid="waitlist-error">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">First Name</Label>
                    <Input value={form.first_name} onChange={update("first_name")} required placeholder="John" className="glass-input rounded-xl h-11" data-testid="first-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">Last Name</Label>
                    <Input value={form.last_name} onChange={update("last_name")} required placeholder="Doe" className="glass-input rounded-xl h-11" data-testid="last-name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/60 text-sm font-medium">Email</Label>
                  <Input type="email" value={form.email} onChange={update("email")} required placeholder="you@example.com" className="glass-input rounded-xl h-11" data-testid="waitlist-email-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/60 text-sm font-medium">Phone Number</Label>
                  <Input type="tel" value={form.phone} onChange={update("phone")} required placeholder="+1 (555) 000-0000" className="glass-input rounded-xl h-11" data-testid="phone-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/25 text-sm">Referred by</Label>
                  <Input value={ambassador?.affiliate_code || code} disabled className="glass-input rounded-xl h-11 font-mono text-sm !text-[#F7941D] !opacity-100 cursor-not-allowed" data-testid="referral-code-display" />
                </div>
                <button type="submit" className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-12 text-base transition-all duration-300 hover:shadow-[0_0_20px_rgba(247,148,29,0.3)] disabled:opacity-50" disabled={submitting} data-testid="join-waitlist-button">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
                  {submitting ? "Joining..." : "Join Waitlist"}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <div className="glass rounded-2xl p-5">
              <h3 className="font-semibold text-white/80 text-sm mb-3">What you'll get</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-[#F7941D] mt-0.5 shrink-0" />
                  <span className="text-sm text-white/45"><span className="font-medium text-[#F7941D]">$50 credit</span> when Studio launches on June 1st</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-[#F7941D] mt-0.5 shrink-0" />
                  <span className="text-sm text-white/45">Early access to the Closed Beta</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-[#F7941D] mt-0.5 shrink-0" />
                  <span className="text-sm text-white/45">Cloud IDE with full-context AI (GPT-5.2, Claude, Gemini)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-[#F7941D] mt-0.5 shrink-0" />
                  <span className="text-sm text-white/45">One-click deploys, real-time collaboration, zero local setup</span>
                </li>
              </ul>
            </div>
            <div className="glass rounded-2xl p-5 border-[#F7941D]/20">
              <p className="text-sm text-white/70 font-medium mb-1">Eamico Studio</p>
              <p className="text-xs text-white/35">Closed Beta — June 1, 2026</p>
              <p className="text-xs text-white/25 mt-3">The cloud IDE that replaces your local setup, CI pipeline, and deployment scripts. Everything in one tab.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] py-6">
        <p className="text-center text-xs text-white/20">Eamico Inc. &middot; Write, ship, and scale.</p>
      </footer>
    </div>
  );
}
